import React, { useEffect, useState } from "react";
// reactstrap components
import { Card, CardBody, Row, Col, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { getCookie } from './utils';

import HtmlContent from "components/Custom/htmlContent";
import content from 'content.json'; // Importing the JSON file

function Features() {
  const brandname = content.brandname;
  const [highleveldesc, setHighleveldesc] = useState('');
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const login_api_url = process.env.REACT_APP_LOGIN_API_URL;
  const resource_api_url = process.env.REACT_APP_RESOURCE_API_URL;

  useEffect(() => {
    checkUserStatus();
    
    // Check payment status again after 2 seconds to handle post-payment redirects
    const paymentCheckTimer = setTimeout(() => {
      if (isAuthenticated) {
        checkPaymentStatus();
      }
    }, 2000);

    // No YouTube API needed for iframe approach

    return () => {
      clearTimeout(paymentCheckTimer);
      if (window.onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, [isAuthenticated]);

  const checkUserStatus = async () => {
    const token = getCookie(`${brandname}_token`);
    
    if (token) {
      try {
        // Check if user is authenticated
        const authResponse = await axios.get(`${login_api_url}/api/CheckToken`, {
          headers: {
            Authorization: token,
          }
        });
        
        if (authResponse.data.userid) {
          // Use email if available, otherwise fallback to userid
          const userIdentifier = authResponse.data.email || authResponse.data.userid;
          setUser(userIdentifier);
          setIsAuthenticated(true);
          
          // Check premium status
          await checkPaymentStatus();
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const checkPaymentStatus = async () => {
    const token = getCookie(`${brandname}_token`);
    if (token) {
      try {
        const paymentResponse = await axios.get(`${resource_api_url}/api/CheckPayment`, {
          headers: {
            Authorization: token,
          }
        });
        setIsPremium(paymentResponse.data.has_paid);
      } catch (error) {
        setIsPremium(false);
      }
    }
  };

  const handleGetStarted = () => {
    navigate('/admin/getactivities');
  };

  // brandname loaded for display

  return (
    <>
      <div className="content">
        {/* Dark Theme Hero Section */}
        <div style={{
          backgroundColor: '#000',
          color: '#fff',
          padding: '0px 0px',
          marginBottom: '0px',
          fontFamily: 'monospace, "Courier New", monospace'
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ textAlign: 'center', margin: '20px 0 16px' }}>
              <div className="data-update-banner">
                🎉&nbsp; Updated for ADE 2026 — new lineups &amp; events loaded
              </div>
            </div>

            <h1 style={{
              fontSize: 'clamp(0.9rem, 3vw, 1.4rem)',
              fontWeight: 'bold',
              margin: '0 0 0px 0',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              background: 'linear-gradient(90deg, #D97706 0%, #FBBF24 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center'
            }}>
              THE DANCE EVENT FESTIVAL IN AMSTERDAM 2026
            </h1>

            <p style={{
              fontSize: 'clamp(0.8rem, 2.5vw, 1rem)',
              color: '#fff',
              margin: '0 0 15px 0',
              textAlign: 'center',
              letterSpacing: '1px'
            }}>
              Five days • 1,100+ events • 3,500+ artists • 285+ venues
            </p>
            
            <div style={{
              marginBottom: '15px'
            }}>
              <p style={{
                fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                lineHeight: '1.6',
                color: '#fff',
                margin: '0 0 15px 0',
                letterSpacing: '0.5px',
                textAlign: 'center'
              }}>
                Amsterdam transforms into the world's most inspiring clubbing destination during this five-day festival.
              </p>
              <p style={{
                fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                lineHeight: '1.6',
                color: '#fff',
                margin: '0',
                letterSpacing: '0.5px',
                textAlign: 'center'
              }}>
                Connect your Spotify playlist to discover which events feature your favorite artists and plan your perfect schedule.
              </p>
            </div>
            
            <div style={{
              borderTop: '1px solid #333',
              paddingTop: '10px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '0.9rem',
                color: '#fff',
                margin: '0',
                letterSpacing: '1px'
              }}>
                ↓ Start planning your festival experience below ↓
              </p>
            </div>
          </div>
        </div>
        {!loading && isAuthenticated && (
          <Row>
            <Col md="12">
              <Card className="mb-4">
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h4 className="mb-1">Welcome back, {user}! 👋</h4>
                      {isPremium ? (
                        <p className="text-success mb-0">
                          <i className="fas fa-crown mr-2"></i>
                          You have <strong>Premium Access</strong> - Ready to discover your perfect events!
                        </p>
                      ) : (
                        <p className="text-muted mb-0">
                          <i className="fas fa-music mr-2"></i>
                          Start exploring events that match your Spotify taste
                        </p>
                      )}
                    </div>
                    {isPremium && (
                      <div className="text-right">
                        <span className="badge badge-success badge-lg">
                          ⭐ Premium Member
                        </span>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Premium User Encouragement */}
        {!loading && isAuthenticated && isPremium && (
          <Row>
            <Col md="12" className="text-center mb-4">
              <div className="alert alert-success" role="alert">
                <h5 className="alert-heading">
                  <i className="fas fa-rocket mr-2"></i>
                  Ready to explore your personalized events?
                </h5>
                <p className="mb-3">
                  As a premium member, you have unlimited access to all your track matches and events. 
                  Click the button below to start discovering which Amsterdam Events feature your favorite artists!
                </p>
                <Button
                  color="success"
                  size="lg"
                  onClick={handleGetStarted}
                  className="get-started-btn"
                  style={{
                    fontSize: '1.2rem',
                    padding: '12px 30px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="fas fa-calendar-alt mr-2"></i>
                  Start Exploring Your Events
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {/* Default Get Started Button for non-premium or non-authenticated users */}
        {(!isAuthenticated || !isPremium) && (
          <Row>
            <Col md="12" className="text-center mb-4">
              <Button
                color="primary"
                size="lg"
                onClick={handleGetStarted}
                className="get-started-btn"
                style={{
                  fontSize: '1.2rem',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
                  transition: 'all 0.3s ease'
                }}
              >
                Get Started Here
              </Button>
              
                  
              
                  
                  
                  <br />
                  
                  {/* <HtmlContent html={content.features.features} />
                  <HtmlContent html={content.features.benefits} /> */}
                
            </Col>
          </Row>
        )}
                                                     {/* Video Section */}
          <Row>
            <Col md="12">
              <Card style={{ border: '2px solid #F59E0B', borderRadius: '12px', backgroundColor: '#1a1a1a' }}>
                <CardBody style={{ padding: '20px' }}>
                  {/* Simple video container */}
                  <div style={{ 
                    position: 'relative', 
                    width: '100%', 
                    height: '800px',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <iframe
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                      src="https://www.youtube.com/embed/irBUe8nbnJM"
                      title="September 1, 2025"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        <Row>
          <Col md="10">
            <Card>
              <CardBody>
                <div className="typography-line">
                  <HtmlContent html={content.features.intro} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Features;
